/**
 * Date/time parsing + formatting helpers used across the app.
 *
 * **Why a wrapper around `new Date(string)`.** JavaScript's `Date`
 * constructor parses ISO 8601 strings that have explicit timezone
 * info (`Z` or `±HH:MM`) as absolute instants — correct. But it
 * parses strings that LACK timezone info (`"2026-05-26T14:30:00"`,
 * `"2026-05-26 14:30:00"`) as LOCAL time, which silently shifts
 * every displayed timestamp by the user's UTC offset.
 *
 * The backend stores some `_at` columns as `timestamp without time
 * zone` and historically serialized them via `to_string()`, which
 * produces formats like `"2026-05-26 14:30:00 +00:00"` (note the
 * space, not `T`) — some browsers parse that, most don't. Even
 * after fixing the serialization, we keep `parseTimestamp` defensive
 * so a partial deploy doesn't break the UI.
 *
 * **Policy.** When in doubt, treat the value as UTC. Anything the
 * backend emits is wall-clock UTC by convention; an unspecified-tz
 * string means "I forgot to attach `Z`" not "this is local time."
 */

/**
 * Parse a timestamp string into a `Date`. Tolerates:
 *  - RFC 3339 with `Z` or `±HH:MM`            (correct, pass-through)
 *  - ISO 8601 without timezone                (treated as UTC)
 *  - `"YYYY-MM-DD HH:MM:SS"` (space, no `T`)  (normalized + UTC)
 *  - `"YYYY-MM-DD HH:MM:SS UTC"`              (normalized + UTC)
 *  - `"YYYY-MM-DD HH:MM:SS +00:00"`           (normalized to ISO)
 *
 * Returns `null` for empty/null/invalid input so callers can render
 * a placeholder instead of `Invalid Date`.
 */
export function parseTimestamp(input: string | null | undefined): Date | null {
  if (input == null) return null;
  const s = input.trim();
  if (!s) return null;

  // Already explicit-timezone — pass straight through.
  // RFC 3339 ends with `Z` or `±HH:MM` (or `±HHMM`).
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // Chrono's `DateTime<Utc>::to_string()` form: "2026-05-26 14:30:00 UTC".
  // Strip the trailing " UTC" and add a `Z` so JS parses as absolute.
  const utcSuffix = s.replace(/\s+UTC$/, '');
  if (utcSuffix !== s) {
    const d = new Date(`${utcSuffix.replace(' ', 'T')}Z`);
    return isNaN(d.getTime()) ? null : d;
  }

  // Postgres-style "YYYY-MM-DD HH:MM:SS[.fff]" with no tz — assume UTC.
  // Replace the space separator with `T` to make it a valid ISO 8601.
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) {
    const iso = s.replace(' ', 'T');
    // If still no tz suffix, append Z.
    const withTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
    const d = new Date(withTz);
    return isNaN(d.getTime()) ? null : d;
  }

  // Last resort — let the browser try. Returns null on Invalid Date.
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Resolve the user's IANA time-zone (e.g. `"America/New_York"`).
 * Falls back to `"UTC"` if `Intl` isn't available — should be
 * impossible in any supported browser, but the fallback keeps
 * server-rendered/test paths working.
 */
export function userTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Build the absolute-time string we show in the `<RelativeTime>`
 * tooltip: local-wall-clock time with the IANA zone abbreviation,
 * plus the UTC equivalent on a second line. Operators routinely
 * compare a UI timestamp against a server log entry that's in UTC,
 * so showing both removes the mental conversion.
 *
 * Example output:
 *   "May 26, 2026, 10:30:00 AM EDT
 *    2026-05-26 14:30:00 UTC"
 */
export function formatAbsoluteTooltip(date: Date): string {
  const localStr = date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  const utcStr = `${date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')} UTC`;
  return `${localStr}\n${utcStr}`;
}
